import {
  ActionIcon,
  Button,
  Group,
  Select,
  Stack,
  Text,
  TextInput,
  Tooltip,
  Popover,
  NumberInput,
  Divider,
  Menu,
} from "@mantine/core";
import {
  IconBell,
  IconPlus,
  IconClock,
  IconChevronDown,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useLanguage } from "../../../../../../../stores/LanguageStore";
import {
  useLeadsContainerStore,
  useLeadsSheetStore,
} from "../../../../../../../stores/LeadsStore";
import {
  createReminder,
  updateProspect,
} from "../../../../../../../api/PRMAPI";
import dayjs from "dayjs";
import { notifications } from "@mantine/notifications";
import { useProfileStore } from "../../../../../../../stores/UserStore";
import { modals } from "@mantine/modals";
import { reminderTypes } from "../../../../utils";
import { DateTimePicker } from "@mantine/dates";

const defaultCallType = "phone";

const AddReminder = ({ inLeft, isMobile }) => {
  const lang = useLanguage((s) => s.language);
  const current = useLeadsSheetStore((s) => s.current);
  const setCurrent = useLeadsSheetStore((s) => s.setCurrent);
  const currentContainer = useLeadsSheetStore((s) => s.currentContainer);
  const setIndex = useLeadsSheetStore((s) => s.setIndex);
  const setCurrentContainer = useLeadsSheetStore((s) => s.setCurrentContainer);
  const updateContainer = useLeadsContainerStore((s) => s.updateContainer);
  const containers = useLeadsContainerStore((s) => s.containers);
  const [loading, setLoading] = useState(false);

  // Nouveaux états pour le Popover de rappel rapide
  const [popoverOpened, setPopoverOpened] = useState(false);
  const [amount, setAmount] = useState(15);
  const [unit, setUnit] = useState("minutes");

  // Options pour les unités de temps
  const unitOptions = useMemo(
    () => [
      { value: "minutes", label: lang.prm.details.reminders.minutes },
      { value: "hours", label: lang.prm.details.reminders.hour },
      { value: "days", label: lang.prm.details.reminders.day },
      { value: "months", label: lang.prm.details.reminders.month },
      { value: "years", label: lang.prm.details.reminders.year },
    ],
    [lang],
  );

  const quickReminders = useMemo(
    () => [
      {
        label: `${lang.prm.details.reminders.in} 15 ${lang.prm.details.reminders.minutes}`,
        value: 15,
      },
      {
        label: `${lang.prm.details.reminders.in} 30 ${lang.prm.details.reminders.minutes}`,
        value: 30,
      },
      {
        label: `${lang.prm.details.reminders.in} 1 ${lang.prm.details.reminders.hour}`,
        value: 60,
      },
      {
        label: `${lang.prm.details.reminders.in} 2 ${lang.prm.details.reminders.hour}s`,
        value: 60 * 2,
      },
      {
        label: `${lang.prm.details.reminders.in} 1 ${lang.prm.details.reminders.day}`,
        value: 60 * 24,
      },
      {
        label: `${lang.prm.details.reminders.in} 1 ${lang.prm.details.reminders.week}`,
        value: 60 * 24 * 7,
      },
      {
        label: `${lang.prm.details.reminders.in} 2 ${lang.prm.details.reminders.week}s`,
        value: 60 * 24 * 7 * 2,
      },
      {
        label: `${lang.prm.details.reminders.in} 1 ${lang.prm.details.reminders.month}`,
        value: 60 * 24 * 30,
      },
      {
        label: `${lang.prm.details.reminders.in} 3 ${lang.prm.details.reminders.month}`,
        value: 60 * 24 * 30 * 3,
      },
      {
        label: `${lang.prm.details.reminders.in} 6 ${lang.prm.details.reminders.month}`,
        value: 60 * 24 * 30 * 6,
      },
      {
        label: `${lang.prm.details.reminders.in} 1 ${lang.prm.details.reminders.year}`,
        value: 60 * 24 * 365,
      },
      {
        label: lang.prm.details.reminders.manual,
        value: "manual",
      },
    ],
    [lang],
  );

  const handleQuickClick = (value) => {
    if (value !== "manual") {
      return handleCreate({ value });
    } else {
      modals.open({
        title: (
          <Text>
            {lang.prm.details.reminders.dialog_title}{" "}
            {current?.["%first_name%"] || lang.prm.details.reminders.prospect}{" "}
            {current?.["%last_name%"] || `#${current?.id}`}
          </Text>
        ),
        children: (
          <Stack
            component="form"
            id="create-reminder-form"
            onSubmit={onSubmit}
            gap={"xs"}
          >
            <TextInput
              data-autofocus
              name="name"
              label={lang.prm.details.reminders.name}
              placeholder={lang.prm.details.reminders.name}
              required
            />
            <DateTimePicker
              label={lang.prm.details.reminders.date}
              defaultValue={new Date()}
              minDate={new Date()}
              name="date"
              required
              dropdownType="modal"
              modalProps={{
                zIndex: 9000,
              }}
            />
            <Select
              data={Object.keys(reminderTypes()).map((key) => ({
                label: reminderTypes()[key],
                value: key,
              }))}
              label={lang.prm.details.reminders.type}
              defaultValue={"phone"}
              name="type"
            />

            <Group justify="end" mt={"lg"}>
              <Button
                color="primary"
                type="submit"
                leftSection={<IconPlus size={18} />}
              >
                {lang.prm.details.reminders.create}
              </Button>
            </Group>
          </Stack>
        ),
      });
    }
  };

  const handleCreate = ({ value, date, type, name }) => {
    const convertedDate = dayjs().add(value, "m");
    setLoading(true);

    createReminder({
      prospect_id: current.id,
      call_date: date || convertedDate.toISOString(),
      name: name || lang.prm.details.reminders.default_name,
      type: type || defaultCallType,
    })
      .then((response) => {
        const profile = useProfileStore.getState().profile;

        let newCall = {
          id: response.data.call_id,
          call_date: date || convertedDate.toISOString(),
          name: name || lang.prm.details.reminders.default_name,
          type: type || defaultCallType,
          created_by: {
            last_name: profile.last_name,
            first_name: profile.first_name,
            id: profile.id,
          },
        };
        let newItem = {
          ...current,
          calls: [newCall, ...current.calls],
        };

        function handleMove() {
          updateProspect({ id: current.id, status: "to_call" });
          setCurrent({ ...newItem, status: "to_call" });
          // Remove from current container
          let newItemsLast = currentContainer?.items?.filter(
            (item) => item.id !== current.id,
          );
          updateContainer(currentContainer.id, {
            items: newItemsLast,
            total: currentContainer.total - 1,
            needCacheReset: true,
          });

          // Add to to_call
          let newContainer = containers.find((x) => x.id === "to_call");
          let newItemsFuture = [...newContainer.items];
          newItemsFuture.unshift({
            ...newItem,
            status_changed_date: new Date(),
          });
          updateContainer("to_call", {
            total: newContainer.total + 1,
            items: newItemsFuture,
            needCacheReset: true,
          });
          setCurrentContainer({
            ...newContainer,
            total: newContainer.total + 1,
            items: newItemsFuture,
          });
          setIndex(0);
          notifications.show({
            message: lang.prm.details.reminders.moved_to_call,
          });
        }

        if (current.status !== "to_call" && !current.custom_status) {
          modals.openConfirmModal({
            modalId: "to-call",
            withCloseButton: false,
            children: <Text>{lang.prm.move_to_call}</Text>,
            centered: true,
            labels: { confirm: lang.global.yes, cancel: lang.global.no },
            onConfirm: () => handleMove(),
          });
        }
        setCurrent(newItem);
        setPopoverOpened(false); // Fermer le popover en cas de succès
      })
      .finally(() => setLoading(false));
  };

  // Convertir le choix du popover en minutes pour le backend
  const handleQuickSubmit = () => {
    let minutesToAdd = amount;
    switch (unit) {
      case "hours":
        minutesToAdd = amount * 60;
        break;
      case "days":
        minutesToAdd = amount * 60 * 24;
        break;
      case "months":
        minutesToAdd = amount * 60 * 24 * 30;
        break;
      case "years":
        minutesToAdd = amount * 60 * 24 * 365;
        break;
      default:
        minutesToAdd = amount; // minutes
    }
    handleCreate({ value: minutesToAdd });
  };

  const onSubmit = (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const data = Object.fromEntries(fd);

    modals.closeAll();
    return handleCreate({
      date: data?.date,
      type: data?.type,
      name: data?.name,
    });
  };

  const openManualModal = () => {
    setPopoverOpened(false);
    modals.open({
      title: (
        <Text>
          {lang.prm.details.reminders.dialog_title}{" "}
          {current?.["%first_name%"] || lang.prm.details.reminders.prospect}{" "}
          {current?.["%last_name%"] || `#${current?.id}`}
        </Text>
      ),
      children: (
        <Stack
          component="form"
          id="create-reminder-form"
          onSubmit={onSubmit}
          gap={"xs"}
        >
          <TextInput
            data-autofocus
            name="name"
            label={lang.prm.details.reminders.name}
            placeholder={lang.prm.details.reminders.name}
            required
          />
          <DateTimePicker
            label={lang.prm.details.reminders.date}
            defaultValue={new Date()}
            minDate={new Date()}
            name="date"
            required
            dropdownType="modal"
            modalProps={{
              zIndex: 9000,
            }}
          />
          <Select
            data={Object.keys(reminderTypes()).map((key) => ({
              label: reminderTypes()[key],
              value: key,
            }))}
            label={lang.prm.details.reminders.type}
            defaultValue={"phone"}
            name="type"
          />

          <Group justify="end" mt={"lg"}>
            <Button
              color="primary"
              type="submit"
              leftSection={<IconPlus size={18} />}
            >
              {lang.prm.details.reminders.create}
            </Button>
          </Group>
        </Stack>
      ),
    });
  };

  return (
    <Popover
      opened={popoverOpened}
      onChange={setPopoverOpened}
      withArrow
      offset={4}
      withinPortal={false}
      trapFocus
    >
      {inLeft ? (
        <Popover.Target>
          <Tooltip label={lang.leads.create_reminder}>
            <ActionIcon
              variant="light"
              size={"md"}
              style={{ pointerEvents: "none" }}
              onClick={() => setPopoverOpened((o) => !o)}
            >
              <IconBell size={16} />
            </ActionIcon>
          </Tooltip>
        </Popover.Target>
      ) : (
        <Popover.Target>
          <Button
            variant="filled"
            color="primary"
            leftSection={<IconPlus size={18} />}
            loading={loading}
            size={isMobile ? "xs" : "md"}
            onClick={() => setPopoverOpened((o) => !o)}
            rightSection={
              isMobile ? null : (
                <Menu withArrow offset={0} withinPortal={true} portalProps={{target: "#reminders-tab"}}>
                  <Menu.Target>
                    <ActionIcon variant="filled" size={"md"} onClick={e => e.stopPropagation()}>
                      <IconChevronDown size={16} />
                    </ActionIcon>
                  </Menu.Target>

                  <Menu.Dropdown>
                    {quickReminders.map((item) => (
                      <Menu.Item
                        key={item.value}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleQuickClick(item.value);
                        }}
                      >
                        {item.label}
                      </Menu.Item>
                    ))}
                  </Menu.Dropdown>
                </Menu>
              )
            }
          >
            {lang.leads.create_reminder}
          </Button>
        </Popover.Target>
      )}

      <Popover.Dropdown p="sm">
        <Stack gap="sm">
          <Text size="sm" fw={500}>
            {lang.prm.details.reminders.in}
          </Text>

          <Group wrap="nowrap" gap="xs">
            <NumberInput
              value={amount}
              onChange={(val) => setAmount(val || 1)}
              min={1}
              w={80}
              allowNegative={false}
              allowDecimal={false}
            />
            <Select
              value={unit}
              onChange={setUnit}
              data={unitOptions}
              w={140}
              allowDeselect={false}
              comboboxProps={{ withinPortal: false }}
            />
          </Group>

          <Button
            onClick={handleQuickSubmit}
            loading={loading}
            leftSection={<IconClock size={16} />}
            fullWidth
            mt="xs"
          >
            {lang.prm.details.reminders.create}
          </Button>

          <Divider my={4} />

          <Button variant="light" fullWidth onClick={openManualModal}>
            {lang.prm.details.reminders.manual}
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default AddReminder;
